/* =====================================================================================
   Attachments — one component for every module that needs files.

   Studio assets, grant application forms, controlled documents and contracts all want the
   same thing and differ only in which bucket they use and what a file MEANS. That last part
   is the `role`, and it matters most in Grants: a draft is not a submitted form, and
   confusing the two at audit is a real problem rather than a tidiness one.

   Drag and drop as well as a file picker, because at a congress people drag a photo of a
   poster onto the screen and expect it to work.
   ===================================================================================== */

import { useEffect, useRef, useState, type DragEvent } from 'react';
import {
  fetchAttachments, uploadFile, signedUrl, removeAttachment, humanSize,
  type Attachment, type Bucket, type AttachmentRole,
} from '../lib/uploads';
import './attachments.css';

interface Props {
  objectType: string;
  objectId: string;
  bucket: Bucket;
  /** Offer these meanings. Omit for a plain attachment list. */
  roles?: AttachmentRole[];
  label?: string;
  accept?: string;
  readOnly?: boolean;
  /** Ask for a one-line description with each upload. Opt in, because most modules attach
      a file to a record that already names it; Circus opts in because its documents are
      only findable by what somebody said they were. */
  describe?: boolean;
}

export function Attachments({
  objectType, objectId, bucket, roles, label = 'Files', accept, readOnly, describe,
}: Props) {
  const [files, setFiles] = useState<Attachment[]>([]);
  const [role, setRole] = useState<AttachmentRole>(roles?.[0] ?? 'attachment');
  const [desc, setDesc] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement | null>(null);

  async function reload() {
    try { setFiles(await fetchAttachments(objectType, objectId)); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }
  useEffect(() => { void reload(); }, [objectType, objectId]);

  async function take(list: FileList | null) {
    if (!list?.length) return;
    setErr(null);
    // One at a time so a single rejected file names itself rather than failing the batch
    // silently. Uploading ten and being told "something went wrong" is useless.
    for (const file of Array.from(list)) {
      setBusy(file.name);
      try {
        await uploadFile({ file, bucket, objectType, objectId, role,
                           description: describe ? desc : null });
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
        break;
      } finally { setBusy(null); }
    }
    setDesc('');
    await reload();
    if (input.current) input.current.value = '';
  }

  async function open(a: Attachment) {
    try {
      // Private bucket, so a fresh short-lived link each time rather than a stored URL that
      // would either expire in someone's bookmarks or never expire at all.
      window.open(await signedUrl(a.bucket, a.path), '_blank', 'noopener');
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }

  function onDrop(e: DragEvent) {
    e.preventDefault(); setOver(false);
    if (!readOnly) void take(e.dataTransfer.files);
  }

  return (
    <section className="att">
      <header className="att-head">
        <h3>{label}{files.length > 0 && <span className="att-count">{files.length}</span>}</h3>
        {roles && roles.length > 1 && !readOnly && (
          <select value={role} onChange={(e) => setRole(e.target.value as AttachmentRole)}>
            {roles.map((r) => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
          </select>
        )}
      </header>

      {!readOnly && describe && (
        <input
          className="att-describe"
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          maxLength={500}
          placeholder="What is this file? One line, applied to the next upload."
          aria-label="Description for the next upload"
        />
      )}

      {!readOnly && (
        <div
          className={`att-drop ${over ? 'over' : ''} ${busy ? 'busy' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setOver(true); }}
          onDragLeave={() => setOver(false)}
          onDrop={onDrop}
          onClick={() => input.current?.click()}
          role="button" tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && input.current?.click()}
        >
          <input ref={input} type="file" multiple accept={accept} hidden
                 onChange={(e) => take(e.target.files)} />
          {busy ? `Uploading ${busy}…` : 'Drop a file here, or click to choose'}
        </div>
      )}

      {err && <p className="att-err" role="alert">{err}</p>}

      {files.length === 0 && !busy && <p className="att-empty">Nothing attached yet.</p>}

      <ul className="att-list">
        {files.map((a) => (
          <li key={a.id} className="att-item">
            <span className="att-main">
              <button className="att-name" onClick={() => open(a)} title="Open">
                {a.filename}
              </button>
              {a.description && <span className="att-desc">{a.description}</span>}
            </span>
            <span className="att-meta">
              {a.role !== 'attachment' && <span className="pill mute">{a.role}</span>}
              {humanSize(a.bytes)}
              {' · '}{a.uploaded_by_name ?? 'someone'}
              {' · '}{new Date(a.uploaded_at).toLocaleDateString('en-GB')}
            </span>
            {!readOnly && (
              <button className="att-remove"
                      onClick={() => removeAttachment(a.id).then(reload)}
                      title="Withdraw. The file stays retrievable for audit.">
                Withdraw
              </button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
